import { Injectable } from '@angular/core';
import { HttpErrorResponse } from '@angular/common/http';

import { Observable, firstValueFrom } from 'rxjs';

import { ApiService } from './api.service';
import { NewApiService } from './new-api/new-api.service';

import { Utility } from './utility.service';

import {
  CustomErrorMessage,
  ErrorMessage,
  HttpErrorMessage,
  Result,
} from 'src/models/Result';
import { QueryParams } from 'src/models/QueryParams';
import { EmailDispatchData } from 'src/models/EmailDispatchData';
import { Setting } from 'src/models/Setting';
import { TextTemplate } from 'src/models/TextTemplate';
import { IncludeDefinition, QueryFilter } from 'lipp-components';
import { EnterpriseData } from 'src/models/EnterpriseData';
import { ResultOfQueryFilter } from 'src/models/ResultOfQueryFilter';
import { User } from 'src/models/User';
import { Station } from 'src/models/Station';
import { TextTemplatePlaceholder } from 'src/models/TextTemplatePlaceholder';
import { SchedulePeriodPrototype } from 'src/dialogs/schedule/schedule.dialog';
import { Schedule } from 'src/models/Schedule';

import { SettingDTO } from 'src/dtos/SettingDTO';
import { TextTemplateDTO } from 'src/dtos/TextTemplateDTO';
import { EnterpriseDTO } from 'src/dtos/EnterpriseDTO';
import { StationDTO } from 'src/dtos/StationDTO';
import { UserDTO } from 'src/dtos/UserDTO';
import { FileDTO } from 'src/dtos/FileDTO';
import {
  TextTemplatePlaceholderDTO,
  TextTemplatePlaceholdersDTO,
} from 'src/dtos/TextTemplatePlaceholdersDTO';
import { SetSettingDTO } from 'src/dtos/SetSettingDTO';
import { CreateScheduleDTO } from 'src/dtos/CreateScheduleDTO';
import { EditScheduleDTO } from 'src/dtos/EditScheduleDTO';
import { ScheduleDTO } from 'src/dtos/ScheduleDTO';
import { StationModuleDTO } from 'src/dtos/payment/module/StationModuleDTO';

import { EnStationTypes } from 'src/enums/EnStationTypes';

@Injectable({ providedIn: 'root' })
export class ClientsService {
  constructor(private api: ApiService, private newApi: NewApiService) {}

  public getEmailDispatchData(
    stationId: number
  ): Observable<EmailDispatchData> {
    return new Observable<EmailDispatchData>((observer) => {
      this.api
        .post<SettingDTO[]>(
          `CoreData/Settings/GetAllBeginningWithKey?key=${'Messaging.Email'}`,
          { stationId: stationId }
        )
        .subscribe(
          (settingDTOs) => {
            const settings: Setting[] = settingDTOs.map((settingDTO) => {
              const setting: Setting = new Setting(settingDTO);
              setting.key = setting.key.replace('Messaging.Email.', '');
              return setting;
            });

            const emailDispatchData: EmailDispatchData = {
              senderName: (settings.find(
                (setting) => setting.key == 'SenderName'
              )?.value ?? null) as string | null,
              senderAddress: (settings.find(
                (setting) => setting.key == 'SenderAddress'
              )?.value ?? null) as string | null,
              replyToAddress: (settings.find(
                (setting) => setting.key == 'ReplyToAddress'
              )?.value ?? null) as string | null,
              smtpServer: (settings.find(
                (setting) => setting.key == 'SmtpServer'
              )?.value ?? null) as string | null,
              smtpPort: (settings.find((setting) => setting.key == 'SmtpPort')
                ?.value ?? null) as number | null,
              useAuthentication: (settings.find(
                (setting) => setting.key == 'UseAuthentication'
              )?.value ?? null) as boolean | null,
              smtpUserName: (settings.find(
                (setting) => setting.key == 'SmtpUserName'
              )?.value ?? null) as string | null,
              smtpPassword: (settings.find(
                (setting) => setting.key == 'SmtpPassword'
              )?.value ?? null) as string | null,
            };

            observer.next(emailDispatchData);
            observer.complete();
          },
          (error: HttpErrorResponse) => {
            observer.error([new HttpErrorMessage(error.status)]);
          }
        );
    });
  }

  public validateMailSettings(
    stationId: number,
    emailDispatchData: EmailDispatchData
  ): Observable<null> {
    return new Observable<null>((observer) => {
      const dto = {
        senderEmailAddress: emailDispatchData.senderAddress,
        senderName: emailDispatchData.senderName,
        host: emailDispatchData.smtpServer,
        port: emailDispatchData.smtpPort,
        useAuthentication: emailDispatchData.useAuthentication,
        username: emailDispatchData.smtpUserName,
        password: emailDispatchData.smtpPassword,
        replyToEmailAddress: emailDispatchData.replyToAddress,
      };

      this.api
        .post<Result<boolean>>(
          `Clients/Stations/ValidateMailSettings?stationId${stationId}`,
          dto
        )
        .subscribe(
          (result) => {
            if (result.ok) {
              observer.next(null);
              observer.complete();
            } else {
              observer.error(result.errors);
            }
          },
          (error: HttpErrorResponse) => {
            observer.error([new HttpErrorMessage(error.status)]);
          }
        );
    });
  }

  public changeEmailDispatchData(
    stationId: number,
    emailDispatchData: EmailDispatchData
  ): Observable<null> {
    return new Observable<null>((observer) => {
      const settingDTOs: SetSettingDTO[] = [];

      Object.keys(emailDispatchData).forEach((key) => {
        settingDTOs.push({
          value: (emailDispatchData as any)[key].toString(),
          key: 'Messaging.Email.' + key.charAt(0).toUpperCase() + key.slice(1),
          level: 100,
          stationId: stationId,
        });
      });

      this.api
        .post<Result<boolean>>('CoreData/Settings/SetMultiple', settingDTOs)
        .subscribe(
          (result) => {
            if (result.ok && result.value) {
              observer.next(null);
              observer.complete();
            } else {
              observer.error(result.errors);
            }
          },
          (error: HttpErrorResponse) => {
            observer.error([new HttpErrorMessage(error.status)]);
          }
        );
    });
  }

  public sendTestMail(
    stationId: number,
    email: string,
    message: string
  ): Observable<null> {
    return new Observable<null>((observer) => {
      const queryParams: QueryParams = [
        { stationId: stationId },
        { recipient: email },
      ];

      this.api
        .post<Result<boolean>>(
          `Clients/Stations/SendTestMail${this.api.buildQueryParamsString(
            queryParams
          )}`,
          `"${message}"`
        )
        .subscribe(
          (result) => {
            if (result.ok && result.value) {
              observer.next(null);
              observer.complete();
            } else {
              observer.error(result.errors);
            }
          },
          (error: HttpErrorResponse) => {
            observer.error([new HttpErrorMessage(error.status)]);
          }
        );
    });
  }

  public getTextTemplatesForStationId(
    stationId: number,
    queryFilter: QueryFilter
  ): Observable<TextTemplate[]> {
    return new Observable<TextTemplate[]>((observer) => {
      this.api
        .post<ResultOfQueryFilter<TextTemplateDTO>>(
          `CoreData/TextTemplates/GetTextTemplatesForStationId?stationId=${stationId}`,
          queryFilter
        )
        .subscribe(
          (result) => {
            if (result.ok) {
              const textTemplates: TextTemplate[] = result.value!.data.map(
                (textTemplateDTO) => new TextTemplate(textTemplateDTO)
              );

              observer.next(textTemplates);
              observer.complete();
            } else {
              observer.error(result.errors);
            }
          },
          (error: HttpErrorResponse) => {
            observer.error([new HttpErrorMessage(error.status)]);
          }
        );
    });
  }

  public getTextTemplatePlaceholders(
    textTemplateIdentifier: string
  ): Observable<TextTemplatePlaceholder[]> {
    return new Observable<TextTemplatePlaceholder[]>((observer) => {
      // Todo: sollte ich hier nicht eigentlich ein TextTemplatePlaceholdersDTO[] bekommen?
      this.api
        .get<Result<TextTemplatePlaceholdersDTO>>(
          `CoreData/TextTemplates/GetTextTemplatePlaceholder?identifierName=${textTemplateIdentifier}`
        )
        .subscribe(
          (result) => {
            if (result.ok) {
              const textTemplatePlaceholderDTO =
                Utility.ungzip<TextTemplatePlaceholderDTO>(
                  result.value!.definition
                );

              if (!textTemplatePlaceholderDTO) {
                observer.error([new CustomErrorMessage('UngzipFailed')]);
                return;
              }

              // const placeholders: TextTemplatePlaceholder[] =
              // textTemplatePlaceholderDTOs.map(
              //   (textTemplatePlaceholderDTO) =>
              //     new TextTemplatePlaceholder(textTemplatePlaceholderDTO)
              // );

              const placeholders: TextTemplatePlaceholder[] = [
                new TextTemplatePlaceholder(textTemplatePlaceholderDTO),
              ];

              observer.next(placeholders);
              observer.complete();
            } else {
              observer.error(result.errors);
            }
          },
          (error: HttpErrorResponse) => {
            observer.error([new HttpErrorMessage(error.status)]);
          }
        );
    });
  }

  private createTextTemplate(
    textTemplate: TextTemplate,
    stationId: number
  ): Observable<null> {
    return new Observable<null>((observer) => {
      const body = {
        text: textTemplate.text,
        culture: textTemplate.language,
        stationId: stationId,
        textType: textTemplate.textType,
        type: textTemplate.type,
        description: textTemplate.description,
        name: textTemplate.name,
        identifierName: textTemplate.identifier,
        isLineckerInternal: textTemplate.isLineckerInternal,
        sendNotification: textTemplate.sendNotification,
        canDefineNotificationSending: textTemplate.canDefineNotificationSending,
        notificationType: textTemplate.notificationType,
        notificationRecipientType: textTemplate.notificationRecipientType,
      };

      this.api
        .post<Result<boolean>>(
          'CoreData/TextTemplates/CreateTextTemplate',
          body
        )
        .subscribe(
          (result) => {
            if (result.ok) {
              observer.next(null);
              observer.complete();
            } else {
              observer.error(result.errors);
            }
          },
          (error: HttpErrorResponse) => {
            observer.error([new HttpErrorMessage(error.status)]);
          }
        );
    });
  }

  private editTextTemplate(textTemplate: TextTemplate): Observable<null> {
    return new Observable<null>((observer) => {
      const body = {
        text: textTemplate.text,
      };

      this.api
        .put<Result<boolean>>(
          `CoreData/TextTemplates/EditTextTemplate?id=${textTemplate.id}`,
          body
        )
        .subscribe(
          (result) => {
            if (result.ok) {
              observer.next(null);
              observer.complete();
            } else {
              observer.error(result.errors);
            }
          },
          (error: HttpErrorResponse) => {
            observer.error([new HttpErrorMessage(error.status)]);
          }
        );
    });
  }

  public editTextTemplates(
    textTemplates: TextTemplate[],
    stationId: number
  ): Observable<null> {
    return new Observable<null>((observer) => {
      const requests: Promise<any>[] = textTemplates.map((textTemplate) =>
        firstValueFrom(
          textTemplate.stationId
            ? this.editTextTemplate(textTemplate)
            : this.createTextTemplate(textTemplate, stationId)
        )
      );

      Promise.all(requests)
        .then(() => {
          observer.next(null);
          observer.complete();
        })
        .catch((errorMessages: ErrorMessage[]) => {
          observer.error(errorMessages);
        });
    });
  }

  public resetTextTemplate(
    identifier: string,
    stationId: number
  ): Observable<null> {
    return new Observable<null>((observer) => {
      const queryParams: QueryParams = [
        { identifierName: identifier },
        { stationId: stationId },
      ];

      this.api
        .delete<Result<boolean>>(
          'CoreData/TextTemplates/DeleteTextTemplateByIdentifierNameForStationId',
          queryParams
        )
        .subscribe(
          (result) => {
            if (result.ok) {
              observer.next(null);
              observer.complete();
            } else {
              observer.error(result.errors);
            }
          },
          (error: HttpErrorResponse) => {
            observer.error([new HttpErrorMessage(error.status)]);
          }
        );
    });
  }

  public editTextTemplateSendNotification(
    identifier: string,
    stationId: number,
    sendNotification: boolean
  ): Observable<null> {
    return new Observable<null>((observer) => {
      const body = {
        identifierName: identifier,
        stationId: stationId,
        sendNotification: sendNotification,
      };

      this.api
        .put<Result<boolean>>(
          'CoreData/TextTemplates/SetTextTemplateSendNotification',
          body
        )
        .subscribe(
          (result) => {
            if (result.ok) {
              observer.next(null);
              observer.complete();
            } else {
              observer.error(result.errors);
            }
          },
          (error: HttpErrorResponse) => {
            observer.error([new HttpErrorMessage(error.status)]);
          }
        );
    });
  }

  public getEnterpriseData(enterpriseId: number): Observable<EnterpriseData> {
    return new Observable<EnterpriseData>((observer) => {
      const queryFilter: QueryFilter = {
        includes: [new IncludeDefinition('LogoFile')],
      };

      this.api
        .post<Result<EnterpriseDTO>>(
          `Clients/Enterprises/GetEnterprise?id=${enterpriseId}`,
          queryFilter
        )
        .subscribe(
          (result) => {
            if (result.ok) {
              observer.next(new EnterpriseData(result.value!));
              observer.complete();
            } else {
              observer.error(result.errors);
            }
          },
          (error: HttpErrorResponse) => {
            observer.error([new HttpErrorMessage(error.status)]);
          }
        );
    });
  }

  public getEnterprise(enterpriseId: number): Observable<EnterpriseDTO> {
    return new Observable<EnterpriseDTO>((observer) => {
      const queryFilter: QueryFilter = {
        includes: [new IncludeDefinition('LogoFile')],
      };

      this.api
        .post<Result<EnterpriseDTO>>(
          `Clients/Enterprises/GetEnterprise?id=${enterpriseId}`,
          queryFilter
        )
        .subscribe(
          (result) => {
            if (result.ok) {
              observer.next(result.value!);
              observer.complete();
            } else {
              observer.error(result.errors);
            }
          },
          (error: HttpErrorResponse) => {
            observer.error([new HttpErrorMessage(error.status)]);
          }
        );
    });
  }

  public editEnterprise(
    enterpriseId: number,
    dto: Partial<EnterpriseDTO>
  ): Observable<null> {
    return new Observable<null>((observer) => {
      this.api
        .put<Result<boolean>>(
          `Clients/Enterprises/EditEnterprise?id=${enterpriseId}`,
          dto
        )
        .subscribe(
          (result) => {
            if (result.ok && result.value) {
              observer.next(null);
              observer.complete();
            } else {
              observer.error(result.errors);
            }
          },
          (error: HttpErrorResponse) => {
            observer.error([new HttpErrorMessage(error.status)]);
          }
        );
    });
  }

  public activateEnterprise(enterpriseId: number): Observable<null> {
    const dto: Partial<EnterpriseDTO> = { isActive: true };

    return this.editEnterprise(enterpriseId, dto);
  }

  public deactivateEnterprise(enterpriseId: number): Observable<null> {
    return new Observable<null>((observer) => {
      const queryParams: QueryParams = [{ id: enterpriseId }];

      this.api
        .delete<Result<boolean>>(
          'Clients/Enterprises/DeleteEnterprise',
          queryParams
        )
        .subscribe(
          (result) => {
            if (result.ok) {
              observer.next(null);
              observer.complete();
            } else {
              observer.error(result.errors);
            }
          },
          (error: HttpErrorResponse) => {
            observer.error([new HttpErrorMessage(error.status)]);
          }
        );
    });
  }

  public createStation(
    enterpriseId: number,
    name: string,
    type: EnStationTypes
  ): Observable<number> {
    return new Observable<number>((observer) => {
      const dto = {
        enterpriseId: enterpriseId,
        name: name,
        stationType: type,
      };

      this.api
        .post<Result<number>>('Clients/Stations/CreateStation', dto)
        .subscribe(
          (result) => {
            if (result.ok) {
              observer.next(result.value!);
              observer.complete();
            } else {
              observer.error(result.errors);
            }
          },
          (error: HttpErrorResponse) => {
            observer.error([new HttpErrorMessage(error.status)]);
          }
        );
    });
  }

  public editStation(
    stationId: number,
    dto: Partial<StationDTO>
  ): Observable<null> {
    return new Observable<null>((observer) => {
      this.api
        .put<Result<boolean>>(
          `Clients/Stations/EditStation?id=${stationId}`,
          dto
        )
        .subscribe(
          (result) => {
            if (result.ok && result.value) {
              observer.next(null);
              observer.complete();
            } else {
              observer.error(result.errors);
            }
          },
          (error: HttpErrorResponse) => {
            observer.error([new HttpErrorMessage(error.status)]);
          }
        );
    });
  }

  public activateStation(stationId: number): Observable<null> {
    const dto: Partial<StationDTO> = { isActive: true };

    return this.editStation(stationId, dto);
  }

  public deactivateStation(stationId: number): Observable<null> {
    return new Observable<null>((observer) => {
      const queryParams: QueryParams = [{ id: stationId }];

      this.api
        .delete<Result<boolean>>('Clients/Stations/DeleteStation', queryParams)
        .subscribe(
          (result) => {
            if (result.ok) {
              observer.next(null);
              observer.complete();
            } else {
              observer.error(result.errors);
            }
          },
          (error: HttpErrorResponse) => {
            observer.error([new HttpErrorMessage(error.status)]);
          }
        );
    });
  }

  public getUsersFromAccessibleEnterprisesOfCurrentUser(
    queryFilter: QueryFilter
  ): Observable<User[]> {
    return new Observable<User[]>((observer) => {
      this.api
        .post<ResultOfQueryFilter<UserDTO>>(
          'Clients/Users/GetUsersFromAccessibleEnterprisesOfCurrentUser',
          queryFilter
        )
        .subscribe(
          (result) => {
            if (result.ok) {
              const users: User[] = result.value!.data.map(
                (userDTO) => new User(userDTO)
              );

              observer.next(users);
              observer.complete();
            } else {
              observer.error(result.errors);
            }
          },
          (error: HttpErrorResponse) => {
            observer.error([new HttpErrorMessage(error.status)]);
          }
        );
    });
  }

  public uploadLogo(enterpriseId: number, logo: File): Observable<FileDTO> {
    return new Observable<FileDTO>((observer) => {
      this.api
        .uploadFile<Result<FileDTO>>(
          `Clients/Enterprises/SetLogoFile?id=${enterpriseId}`,
          logo
        )
        .subscribe(
          (result) => {
            if (result.ok) {
              observer.next(result.value!);
              observer.complete();
            } else {
              observer.error(result.errors);
            }
          },
          (error: HttpErrorResponse) => {
            observer.error([new HttpErrorMessage(error.status)]);
          }
        );
    });
  }

  public removeLogo(enterpriseId: number): Observable<null> {
    const queryParams: QueryParams = [{ id: enterpriseId }];

    return new Observable<null>((observer) => {
      // Todo: warum kein delete request?
      this.api
        .get<Result<boolean>>('Clients/Enterprises/ResetLogoFile', queryParams)
        .subscribe(
          (result) => {
            if (result.ok) {
              observer.next(null);
              observer.complete();
            } else {
              observer.error(result.errors);
            }
          },
          (error: HttpErrorResponse) => {
            observer.error([new HttpErrorMessage(error.status)]);
          }
        );
    });
  }

  public changeUserIsEnterpriseAdmin(
    enterpriseId: number,
    userId: number,
    isEnterpriseAdmin: boolean
  ): Observable<null> {
    const queryParams: QueryParams = [
      { enterpriseId: enterpriseId },
      { userId: userId },
      { isEnterpriseAdmin: isEnterpriseAdmin },
    ];

    return new Observable<null>((observer) => {
      // Todo: request ändern
      this.api
        .put<Result<boolean>>(
          `Clients/Users/AddUserIdToEnterpriseId${this.api.buildQueryParamsString(
            queryParams
          )}`,
          {}
        )
        .subscribe(
          (result) => {
            if (result.ok) {
              observer.next(null);
              observer.complete();
            } else {
              observer.error(result.errors);
            }
          },
          (error: HttpErrorResponse) => {
            observer.error([new HttpErrorMessage(error.status)]);
          }
        );
    });
  }

  public createUserGroup(stationId: number, name: string): Observable<number> {
    return new Observable<number>((observer) => {
      const dto = {
        description: name,
      };

      this.api
        .post<Result<number>>(
          `Clients/Users/CreateUserGroupForStationId?stationId=${stationId}`,
          dto
        )
        .subscribe(
          (result) => {
            if (result.ok) {
              const userGroupId: number = result.value!;

              observer.next(userGroupId);
              observer.complete();
            } else {
              observer.error(result.errors);
            }
          },
          (error: HttpErrorResponse) => {
            observer.error([new HttpErrorMessage(error.status)]);
          }
        );
    });
  }

  public editUserGroup(userGroupId: number, name: string): Observable<null> {
    return new Observable<null>((observer) => {
      const dto = {
        description: name,
      };

      this.api
        .put<Result<boolean>>(
          `Clients/Users/EditUserGroup?userGroupId=${userGroupId}`,
          dto
        )
        .subscribe(
          (result) => {
            if (result.ok) {
              observer.next(null);
              observer.complete();
            } else {
              observer.error(result.errors);
            }
          },
          (error: HttpErrorResponse) => {
            observer.error([new HttpErrorMessage(error.status)]);
          }
        );
    });
  }

  public deleteUserGroup(userGroupId: number): Observable<null> {
    return new Observable<null>((observer) => {
      this.api
        .delete<Result<boolean>>(
          `Clients/Users/DeleteUserGroup?userGroupId=${userGroupId}`
        )
        .subscribe(
          (result) => {
            if (result.ok) {
              observer.next(null);
              observer.complete();
            } else {
              observer.error(result.errors);
            }
          },
          (error: HttpErrorResponse) => {
            observer.error([new HttpErrorMessage(error.status)]);
          }
        );
    });
  }

  public removeUserFromEnterprise(
    userId: number,
    enterpriseId: number
  ): Observable<null> {
    return new Observable<null>((observer) => {
      this.api
        .delete<Result<boolean>>(
          `Clients/Users/RemoveUserFromEnterpriseIdByUserId?userId=${userId}&enterpriseId=${enterpriseId}`
        )
        .subscribe(
          (result) => {
            if (result.ok) {
              observer.next(null);
              observer.complete();
            } else {
              observer.error(result.errors);
            }
          },
          (error: HttpErrorResponse) => {
            observer.error([new HttpErrorMessage(error.status)]);
          }
        );
    });
  }

  public getStation(
    stationId: number,
    queryFilter?: QueryFilter
  ): Observable<Result<Station>> {
    return new Observable<Result<Station>>((observer) => {
      const res = new Result<Station>();

      this.api
        .post<Result<StationDTO>>(
          `Clients/Stations/GetStation?id=${stationId}`,
          queryFilter ?? {}
        )
        .subscribe(
          async (result) => {
            res.messages = result.messages;

            if (!res.ok) {
              observer.next(res);
              observer.complete();
              return;
            }

            const stationModulesForStationResult =
              await this.getStationModulesForStationIds([result.value!.id]);

            if (!stationModulesForStationResult.ok) {
              const error = stationModulesForStationResult.errors![0];

              res.messages.push({
                fullKey: error.key,
                key: error.key,
                messageType: 0,
                text: error.text,
                textParameters: error.textParameters,
                textRaw: error.text,
              });

              observer.next(res);
              observer.complete();
              return;
            }

            res.value = new Station(
              result.value!,
              stationModulesForStationResult.value!
            );
            observer.next(res);
            observer.complete();
          },
          (error: HttpErrorResponse) => {
            res.messages.push(new HttpErrorMessage(error.status));
            observer.next(res);
            observer.complete();
          }
        );
    });
  }

  public deleteSchedule(scheduleId: number): Observable<null> {
    return new Observable<null>((observer) => {
      this.api
        .delete<Result<boolean>>(
          `Clients/Stations/DeleteTimePlan?id=${scheduleId}`
        )
        .subscribe(
          (result) => {
            if (result.ok) {
              observer.next(null);
              observer.complete();
            } else {
              observer.error(result.errors);
            }
          },
          (error: HttpErrorResponse) => {
            observer.error([new HttpErrorMessage(error.status)]);
          }
        );
    });
  }

  public createSchedule(dto: CreateScheduleDTO): Observable<number> {
    return new Observable<number>((observer) => {
      this.api
        .post<Result<number>>('Clients/Stations/CreateTimePlan', dto)
        .subscribe(
          (result) => {
            if (result.ok) {
              observer.next(result.value!);
              observer.complete();
            } else {
              observer.error(result.errors);
            }
          },
          (error: HttpErrorResponse) => {
            observer.error([new HttpErrorMessage(error.status)]);
          }
        );
    });
  }

  public editSchedule(dto: EditScheduleDTO): Observable<null> {
    return new Observable<null>((observer) => {
      this.api
        .put<Result<boolean>>(
          `Clients/Stations/EditTimePlan?id=${dto.scheduleId}`,
          dto
        )
        .subscribe(
          (result) => {
            if (result.ok) {
              observer.next(null);
              observer.complete();
            } else {
              observer.error(result.errors);
            }
          },
          (error: HttpErrorResponse) => {
            observer.error([new HttpErrorMessage(error.status)]);
          }
        );
    });
  }

  public checkIfPeriodHasClosingDaysOrSpecialDays(
    period: SchedulePeriodPrototype,
    stationId: number,
    scheduleId?: number
  ): Observable<boolean> {
    return new Observable<boolean>((observer) => {
      const from: string = new Date(
        Date.UTC(
          period.from!.getFullYear(),
          period.from!.getMonth(),
          period.from!.getDate(),
          0,
          0,
          0,
          0
        )
      ).toISOString();

      const to: string = new Date(
        Date.UTC(
          period.from!.getFullYear(),
          period.to!.getMonth(),
          period.to!.getDate(),
          0,
          0,
          0,
          0
        )
      ).toISOString();

      if (scheduleId) {
        const queryParams: QueryParams = [
          { timePlanId: scheduleId },
          { periodFrom: from },
          { periodTo: to },
        ];

        this.api
          .get<Result<boolean>>(
            'Clients/Stations/HasClosingAndSpecialOpeningHoursForTimePlanIdAndPeriod',
            queryParams
          )
          .subscribe(
            (result) => {
              if (result.ok) {
                observer.next(result.value!);
                observer.complete();
              } else {
                observer.error(result.errors);
              }
            },
            (error: HttpErrorResponse) => {
              observer.error([new HttpErrorMessage(error.status)]);
            }
          );
      } else {
        const queryParams: QueryParams = [
          { stationId: stationId },
          { periodFrom: from },
          { periodTo: to },
        ];

        this.api
          .get<Result<boolean>>(
            'Clients/Stations/HasClosingAndSpecialOpeningHoursTimePlansForStationIdAndPeriod',
            queryParams
          )
          .subscribe(
            (result) => {
              if (result.ok) {
                observer.next(result.value!);
                observer.complete();
              } else {
                observer.error(result.errors);
              }
            },
            (error: HttpErrorResponse) => {
              observer.error([new HttpErrorMessage(error.status)]);
            }
          );
      }
    });
  }

  public getSchedules(
    stationId: number,
    queryFilter: QueryFilter
  ): Observable<Schedule[]> {
    return new Observable<Schedule[]>((observer) => {
      this.api
        .post<ResultOfQueryFilter<ScheduleDTO>>(
          `Clients/Stations/GetTimePlansByQueryFilter?stationId=${stationId}`,
          queryFilter
        )
        .subscribe(
          (result) => {
            if (result.ok) {
              const schedules: Schedule[] = result.value!.data.map(
                (scheduleDTO) => new Schedule(scheduleDTO)
              );

              observer.next(schedules);
              observer.complete();
            } else {
              observer.error(result.errors);
            }
          },
          (error: HttpErrorResponse) => {
            observer.error([new HttpErrorMessage(error.status)]);
          }
        );
    });
  }

  public getStationOnlineStatus(
    stationId: number
  ): Observable<Result<StationOnlineStatus>> {
    return new Observable<Result<StationOnlineStatus>>((observer) => {
      const res = new Result<StationOnlineStatus>();

      const body = [{ stationId: stationId }];

      this.api
        .post<Result<any>>(
          'Clients/Stations/GetStationOnlineStatusByStationIds',
          body
        )
        .subscribe(
          (result) => {
            res.messages = result.messages;

            if (!res.ok) {
              observer.next(res);
              observer.complete();
            }

            const isOnline: boolean = result.value[0].isOnline;
            const lastOnlineOnUTC: Date = new Date(
              result.value[0].lastOnlineOnUTC
            );

            res.value = {
              isOnline: isOnline,
              lastOnlineOnUTC: lastOnlineOnUTC,
            };

            observer.next(res);
            observer.complete();
          },
          (error: HttpErrorResponse) => {
            res.messages.push(new HttpErrorMessage(error.status));
            observer.next(res);
            observer.complete();
          }
        );
    });
  }

  public getStationModulesForStationIds(stationIds: number[]) {
    return this.newApi.post<StationModuleDTO[], number[]>(
      'Licensing/GetStationModulesForStationIds',
      stationIds
    );
  }
}

export class StationOnlineStatus {
  public isOnline!: boolean;
  public lastOnlineOnUTC!: Date;
}
